class ProductGrid extends window.BAO.CustomElement() {
  constructor () {
    super()

    this.state = {
      root: window.theme.tagalys.results.stateLayer.state,
    }

    this.initialLoad = false
    this.productCache = new Map()
    this.skippedCount = 0
    this.observer = null

    this._boundHandlers = {
      handleProductsUpdated: products => this.render(products),
    }
  }

  connectedCallback () {
    super.connectedCallback()
    this.subscribeToEvents()
  }

  disconnectedCallback () {
    this.unsubscribeFromEvents()
    this.disconnectObserver()
  }

  subscribeToEvents () {
    window.theme.tagalys.results.eventBus.subscribe(
      'results:state:products:updated',
      this._boundHandlers.handleProductsUpdated,
    )
  }

  unsubscribeFromEvents () {
    window.theme.tagalys.results.eventBus.unsubscribe(
      'results:state:products:updated',
      this._boundHandlers.handleProductsUpdated,
    )
  }

  disconnectObserver () {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }

  productUrl (handle) {
    const root = window.routes?.rootUrl || '/'
    const prefix = root.endsWith('/') ? root : `${root}/`
    return `${prefix}products/${handle}`
  }

  render (products) {
    const gridContainer = this.productGrid
    if (!gridContainer) return

    const requestType = window.theme.tagalys.results.SDK?.helpers.getRequestParams()
      ?.action
    const renderPromos = requestType !== 'APPLY_FILTER'
    const emptyEl = this.emptyEl
    const count = products.length
    const potentialPromos = this._getPotentialPromosForCurrentPage()

    this._returnPromosToHiddenContainer()

    if (count === 0 && potentialPromos.length === 0) {
      gridContainer.innerHTML = ''
      if (emptyEl) emptyEl.hidden = false
      this.updateLastRowItemCount()
      return
    }

    if (emptyEl) emptyEl.hidden = true

    const newSortBy = this.state.root.sortOptions?.find(
      option => option.selected === true,
    )?.id

    if (
      this.initialLoad === false ||
      this.currentSortBy !== newSortBy ||
      this.currentFilters !== JSON.stringify(this.state.root.activeFacets || [])
    ) {
      gridContainer.innerHTML = ''
      this.skippedCount = 0
      this.lowestLoadedPage = undefined
      this.highestLoadedPage = undefined
    }

    this.currentSortBy = newSortBy
    this.currentFilters = JSON.stringify(this.state.root.activeFacets || [])
    this.initialLoad = true

    this.renderProductSkeletons(products)
    this.observeItems()

    if (renderPromos) {
      this._insertPromoCards()
    }

    this.updateLastRowItemCount()
  }

  renderProductSkeletons (products) {
    const fragment = document.createDocumentFragment()

    products.forEach(product => {
      const li = document.createElement('li')
      const url = this.productUrl(product.handle)

      li.classList.add('prd-List_Item')
      li.id = `product-${product.id}`
      li.setAttribute('data-product-grid-el', 'item')
      li.setAttribute('data-collection-filters-form-external-el', 'item')
      li.setAttribute('data-url', url)
      li.setAttribute('data-id', product.id)
      li.setAttribute('data-page', String(this.state.root.pagination.page || 1))
      li.setAttribute('data-loading', 'true')

      const tagalysTitle = window.BAO.TagalysLibrary.getProductTitle(product)
      if (tagalysTitle) {
        li.setAttribute('data-tagalys-title', tagalysTitle)
      }

      li.innerHTML = this.createSkeletonHTML()
      fragment.appendChild(li)
    })

    if (!this.lastPage) {
      this.lastPage = this.state.root.pagination.page
    }

    if (this.lastPage > this.state.root.pagination.page) {
      this.productGrid.prepend(fragment)
    } else {
      this.productGrid.appendChild(fragment)
    }

    this.lastPage = this.state.root.pagination.page
  }

  createSkeletonHTML () {
    return `
      <article class="prd-Card" aria-hidden="true">
        <div class="prd-Card_ImageContainer"></div>
        <div class="prd-Card_Body"></div>
      </article>
    `
  }

  observeItems () {
    this.disconnectObserver()

    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return
          if (entry.target.dataset.loading !== 'true') return

          entry.target.dataset.loading = 'false'
          this.fetchProductWithRetry(entry.target.dataset.url)
            .then(html => {
              if (!html || !html.trim()) {
                entry.target.remove()
                this.skippedCount += 1
                this.updateLastRowItemCount()
                return
              }
              entry.target.innerHTML = html
              this.applyTagalysProductTitle(entry.target)
              document.dispatchEvent(new CustomEvent('collectionRendered'))
            })
            .catch(error => {
              console.error(
                `Error fetching product ${entry.target.dataset.url}:`,
                error,
              )
            })
        })
      },
      {
        root: null,
        rootMargin: '800px',
        threshold: 0,
      },
    )

    this.itemElements.forEach(item => {
      this.observer.observe(item)
    })
  }

  applyTagalysProductTitle (item) {
    if (item.querySelector('[data-has-clean-title]')) return

    const languageCode = window.tagalysPlatformConfiguration?.languageCode
    if (!languageCode || languageCode === 'EN') return

    const title = item.dataset.tagalysTitle
    if (!title) return

    const formattedTitle = title.replace(/-/g, '\u2011')
    const titleEl = item.querySelector('.prd-Card_Title')
    if (titleEl) titleEl.textContent = formattedTitle

    item
      .querySelectorAll('.util-FauxLink_Link .util-ScreenReaderOnly')
      .forEach(el => {
        el.textContent = formattedTitle
      })

    item.querySelectorAll('.prd-Card_Image img').forEach(el => {
      el.setAttribute('alt', formattedTitle)
    })
  }

  async fetchProduct (url) {
    if (!url) return null

    if (this.productCache.has(url)) {
      return this.productCache.get(url)
    }

    const html = await window.BAO.sectionRenderer.fetchSingle(
      this.cardSectionId,
      url,
    )
    const innerHTML = window.BAO.sectionRenderer.getInnerHtml(
      html,
      '.shopify-section',
    )
    this.productCache.set(url, innerHTML)
    return innerHTML
  }

  async fetchProductWithRetry (url, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await this.fetchProduct(url)
      } catch (error) {
        if (i === retries - 1) throw error
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000))
      }
    }
  }

  _returnPromosToHiddenContainer () {
    const gridContainer = this.productGrid
    if (!gridContainer) return

    gridContainer
      .querySelectorAll('[data-inserted-promo="true"]')
      .forEach(promoElement => {
        promoElement.remove()
      })
  }

  get allPromosData () {
    if (!this._allPromosData) {
      const promoDataEl = document.querySelector('[data-promos]')
      try {
        this._allPromosData = promoDataEl
          ? JSON.parse(promoDataEl.textContent.trim())
          : []
      } catch (e) {
        console.error('Failed to parse promo data', e)
        this._allPromosData = []
      }
    }
    return this._allPromosData
  }

  _getPotentialPromosForCurrentPage () {
    const currentPage = this.state.root.pagination.page || 1
    const itemsPerPage = this.productsPerPage
    const firstItemIndexOnPage = (currentPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = currentPage * itemsPerPage
    return (this.allPromosData || []).filter(
      promo =>
        promo.position >= firstItemIndexOnPage &&
        promo.position <= lastItemIndexOnPage,
    )
  }

  _insertPromoCards () {
    const activeFacets = this.state.root.activeFacets || []
    if (activeFacets.length > 0) return

    if (
      this.lowestLoadedPage === undefined ||
      this.state.root.pagination.page < this.lowestLoadedPage
    ) {
      this.lowestLoadedPage = this.state.root.pagination.page
    }

    if (
      this.highestLoadedPage === undefined ||
      this.state.root.pagination.page > this.highestLoadedPage
    ) {
      this.highestLoadedPage = this.state.root.pagination.page
    }

    const itemsPerPage = this.productsPerPage
    const gridContainer = this.productGrid
    const preRenderedPromosContainer = document.getElementById(
      'pre-rendered-promos',
    )
    if (!gridContainer || !preRenderedPromosContainer) return

    const firstItemIndexOnPage = (this.lowestLoadedPage - 1) * itemsPerPage + 1
    const lastItemIndexOnPage = this.highestLoadedPage * itemsPerPage

    const promoWrappersToShow = Array.from(preRenderedPromosContainer.children)
      .filter(promoWrapper => {
        if (!(promoWrapper instanceof HTMLElement)) return false
        const position = parseInt(promoWrapper.dataset.promoPosition, 10)
        return (
          !isNaN(position) &&
          position >= firstItemIndexOnPage &&
          position <= lastItemIndexOnPage
        )
      })
      .sort(
        (a, b) =>
          parseInt(a.dataset.promoPosition, 10) -
          parseInt(b.dataset.promoPosition, 10),
      )

    promoWrappersToShow.forEach(promoWrapper => {
      const position = parseInt(promoWrapper.dataset.promoPosition, 10)
      const promoElement = promoWrapper.cloneNode(true)
      promoElement.dataset.insertedPromo = 'true'

      const targetPageIndex = position - firstItemIndexOnPage
      const elementAtIndex = gridContainer.children[targetPageIndex]

      if (elementAtIndex) {
        gridContainer.insertBefore(promoElement, elementAtIndex)
      } else {
        gridContainer.appendChild(promoElement)
      }
    })
  }

  updateLastRowItemCount () {
    const container = this.productGrid
    if (!container) return

    const columnCount =
      Number(
        getComputedStyle(container)
          .getPropertyValue('--ColumnCount')
          .trim(),
      ) || 4
    const totalItemCount = container.children.length
    const lastRowItemCount = totalItemCount % columnCount
    const pagination = this.state.root.pagination || {}
    const isLastPage =
      pagination.page * pagination.size >= pagination.total ||
      !window.theme.tagalys.results.SDK?.helpers.hasNextPage?.()

    if (isLastPage) {
      container.removeAttribute('data-last-row-item-count')
    } else {
      container.setAttribute(
        'data-last-row-item-count',
        String(lastRowItemCount),
      )
    }
  }

  get productGrid () {
    return this.querySelector('.prd-List_Items')
  }

  get emptyEl () {
    return document.querySelector(
      '[data-collection-filters-form-external-el="empty"]',
    )
  }

  get itemElements () {
    return this.querySelectorAll('[data-product-grid-el="item"]')
  }

  get cardSectionId () {
    return this.dataset.cardSection || 'product-card'
  }

  get productsPerPage () {
    return (
      Number(this.dataset.productsPerPage) ||
      Number(window.theme.resultsConfig?.productsPerPage) ||
      24
    )
  }
}

if (!customElements.get('product-grid')) {
  customElements.define('product-grid', ProductGrid)
}
